/**
 * TaxonomyEditor — Studio modal that assigns discovery taxonomy
 * (main category, country, year, story length, tags, labels) to a
 * single video. Persists via PATCH /api/videos/{id}/taxonomy with
 * the admin token. Also offers a one-click "AI suggest" that calls
 * the backend's gpt-4o-mini classifier and prefills the form.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, Sparkles, Tag as TagIcon, Save, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "storylo_admin_token";

export default function TaxonomyEditor({ video, onClose, onSaved }) {
  const [manifest, setManifest] = useState(null);
  const [form, setForm] = useState({
    main_category: video?.main_category || "",
    country: video?.country || "",
    year: video?.year ?? "",
    story_length: video?.story_length || "",
    tags: video?.tags || [],
    labels: video?.labels || [],
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  // Fetch manifest once when the modal mounts.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/taxonomy`).then(({ data }) => {
      if (!cancelled) setManifest(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Escape to close.
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const _adminToken = () => {
    let t = window.localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!t) {
      t = window.prompt("Enter your admin token:");
      if (t) window.localStorage.setItem(ADMIN_TOKEN_KEY, t);
    }
    return t || "";
  };

  const addTag = (raw) => {
    const s = (raw || "").trim().toLowerCase();
    if (!s) return;
    if (form.tags.includes(s)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, s].slice(0, 24) }));
  };
  const removeTag = (t) =>
    setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const toggleLabel = (slug) =>
    setForm((f) => ({
      ...f,
      labels: f.labels.includes(slug)
        ? f.labels.filter((x) => x !== slug)
        : [...f.labels, slug],
    }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        main_category: form.main_category || null,
        country: form.country?.trim() || null,
        year: form.year === "" || form.year === null ? null : Number(form.year),
        story_length: form.story_length || null,
        tags: form.tags,
        labels: form.labels,
      };
      const { data } = await axios.patch(
        `${API}/videos/${video.id}/taxonomy`,
        payload,
        { headers: { "X-Admin-Token": _adminToken() } }
      );
      setSavedAt(Date.now());
      onSaved?.(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) window.localStorage.removeItem(ADMIN_TOKEN_KEY);
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const aiSuggest = async () => {
    setSuggesting(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/taxonomy/auto-suggest/${video.id}`,
        {},
        { headers: { "X-Admin-Token": _adminToken() } }
      );
      const s = data?.suggestion || {};
      setForm((f) => ({
        main_category: s.main_category || f.main_category,
        country: s.country || f.country,
        year: s.year ?? f.year,
        story_length: s.story_length || f.story_length,
        tags: (s.tags && s.tags.length ? s.tags : f.tags) || [],
        labels: (s.labels && s.labels.length ? s.labels : f.labels) || [],
      }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) window.localStorage.removeItem(ADMIN_TOKEN_KEY);
      setError(e?.response?.data?.detail || e.message || "AI suggest failed");
    } finally {
      setSuggesting(false);
    }
  };

  const countryDatalistId = useMemo(() => `country-suggest-${video?.id || "x"}`, [video]);

  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-8 md:pt-12 pb-8 overflow-y-auto"
      data-testid="taxonomy-editor-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="panel max-w-2xl w-full max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col my-auto">
        <div className="flex items-start justify-between p-6 border-b border-white/5">
          <div>
            <div className="label-eyebrow mb-1 flex items-center gap-2">
              <TagIcon className="w-3.5 h-3.5" /> Categorise story
            </div>
            <div className="font-display text-lg leading-snug">{video.title}</div>
            <div className="text-xs text-[color:var(--muted)] mt-1">
              How this story appears in Discover, Filters and the World Map (coming).
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[color:var(--muted)] hover:text-white"
            data-testid="taxonomy-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {!manifest ? (
            <div className="flex items-center justify-center py-16 text-[color:var(--muted)]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading taxonomy…
            </div>
          ) : (
            <>
              {/* Main category */}
              <div>
                <label className="label-eyebrow mb-2 block">Main category</label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {manifest.main_categories.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, main_category: c.slug }))}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        form.main_category === c.slug
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10"
                          : "border-white/10 hover:border-white/25"
                      }`}
                      data-testid={`taxonomy-cat-${c.slug}`}
                    >
                      <div className="text-sm font-medium">{c.label}</div>
                      <div className="text-[11px] text-[color:var(--muted)] mt-0.5">
                        {c.hint}
                      </div>
                    </button>
                  ))}
                </div>
                {form.main_category && (
                  <button
                    className="mt-2 text-xs text-[color:var(--muted)] hover:text-white"
                    onClick={() => setForm((f) => ({ ...f, main_category: "" }))}
                    type="button"
                  >
                    Clear category
                  </button>
                )}
              </div>

              {/* Country + Year row */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow mb-2 block">Country / region</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                    placeholder="e.g. Spain, Balkans, Global"
                    list={countryDatalistId}
                    className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                    data-testid="taxonomy-country-input"
                  />
                  <datalist id={countryDatalistId}>
                    {manifest.common_countries.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="label-eyebrow mb-2 block">
                    Year (negative = BCE)
                  </label>
                  <input
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                    placeholder="e.g. 1914, -3000"
                    className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                    data-testid="taxonomy-year-input"
                  />
                </div>
              </div>

              {/* Story length */}
              <div>
                <label className="label-eyebrow mb-2 block">Story length</label>
                <div className="flex flex-wrap gap-2">
                  {manifest.story_lengths.map((L) => (
                    <button
                      key={L.slug}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          story_length: f.story_length === L.slug ? "" : L.slug,
                        }))
                      }
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        form.story_length === L.slug
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)]"
                          : "border-white/10 hover:border-white/25"
                      }`}
                      data-testid={`taxonomy-len-${L.slug}`}
                    >
                      {L.label} <span className="opacity-60">· {L.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Labels */}
              <div>
                <label className="label-eyebrow mb-2 block">
                  Labels <span className="text-[color:var(--muted)]">(pick any that apply)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {manifest.story_labels.map((L) => {
                    const on = form.labels.includes(L.slug);
                    return (
                      <button
                        key={L.slug}
                        type="button"
                        onClick={() => toggleLabel(L.slug)}
                        className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-colors"
                        style={{
                          borderColor: on ? L.color : "rgba(255,255,255,0.12)",
                          background: on ? `${L.color}22` : "transparent",
                          color: on ? L.color : "inherit",
                        }}
                        data-testid={`taxonomy-label-${L.slug}`}
                      >
                        {on && "✓ "}
                        {L.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="label-eyebrow mb-2 block">
                  Tags <span className="text-[color:var(--muted)]">(free-form, max 24)</span>
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                  {form.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-white/15 bg-white/5"
                      data-testid={`taxonomy-tag-chip-${t}`}
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => removeTag(t)}
                        className="opacity-60 hover:opacity-100"
                        aria-label={`Remove tag ${t}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addTag(tagInput);
                      setTagInput("");
                    }
                  }}
                  onBlur={() => {
                    if (tagInput.trim()) {
                      addTag(tagInput);
                      setTagInput("");
                    }
                  }}
                  placeholder="Type a tag and press Enter…"
                  className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                  data-testid="taxonomy-tag-input"
                />
              </div>

              {error && (
                <div className="text-xs text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-white/5">
          <button
            type="button"
            onClick={aiSuggest}
            disabled={suggesting || saving || !manifest}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors disabled:opacity-40"
            data-testid="taxonomy-ai-suggest"
          >
            {suggesting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            AI suggest
          </button>
          <div className="flex items-center gap-2">
            {savedAt && Date.now() - savedAt < 5000 && (
              <span className="text-xs text-emerald-400 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Saved
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-2 rounded-lg border border-white/10 hover:border-white/25"
              data-testid="taxonomy-cancel-btn"
            >
              Close
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !manifest}
              className="btn-primary text-xs inline-flex items-center gap-1.5 px-4 py-2 disabled:opacity-40"
              data-testid="taxonomy-save-btn"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save categorisation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
